import {
	Component,
	EventEmitter,
	Input,
	Output
}                               from '@angular/core';
import { ContentAlignmentEnum } from '@models/common.model';
import { fadeInOut }            from '@shared/animations/fadeInOut';
import { DownloadFormatEnum }   from '@shared/components/download-button/download-button.model';
import { SvgTypeEnum }          from '@shared/components/svg/svg.model';
import { ObjectUtils }          from '@utils/helpers';

@Component( {
	selector: 'app-download-button[buttonLabel][supportedDownloadFormats]',
	templateUrl: './download-button.component.html',
	styleUrls: [ './download-button.component.scss' ],
	animations: [ fadeInOut ]
} )
export class DownloadButtonComponent {

	private _supportedDownloadFormats: DownloadFormatEnum[] = [];
	private _defaultDownloadFormat: DownloadFormatEnum;

	selectedDownloadFormat: DownloadFormatEnum;
	multiFormatDownloadButtonIsCollapsed = true;
	contentAlignment: ContentAlignmentEnum;
	contentAlignmentEnum = ContentAlignmentEnum;
	isMultiFormatDownloadButton = false;
	disableDownloadButton = false;

	get supportedDownloadFormats(): DownloadFormatEnum[] {
		return this._supportedDownloadFormats;
	}

	/**
	 * Supported formats - required.
	 */
	@Input() set supportedDownloadFormats( supportedDownloadFormats: DownloadFormatEnum[] ) {
		this._supportedDownloadFormats = supportedDownloadFormats ?? [];
		this.init();
	}

	/**
	 * Label text. Required.
	 */
	@Input() buttonLabel: string;

	/**
	 * CSS class for individual button styling. Optional.
	 */
	@Input() buttonStyleClass: string;

	/**
	 * Show button ? Default is true. Optional.
	 */
	@Input() showButton = true;

	/**
	 * Show icon. Default is managed by number of supported types, but it can be overridden here manually. Optional.
	 */
	@Input() showIcon;

	/**
	 * Svg icon - default is a download icon. Optional.
	 */
	@Input() iconSvgType = SvgTypeEnum.DOWNLOAD;

	/**
	 * RequestInProgress - default false. Optional.
	 */
	@Input() requestInProgress = false;

	/**
	 * Event sends selected format on download click action. Can be null.
	 */
	@Output() buttonEvent = new EventEmitter<DownloadFormatEnum>();

	private init(): void {

		this.disableDownloadButton = false;
		this.multiFormatDownloadButtonIsCollapsed = true;
		this.selectedDownloadFormat = null;
		this._defaultDownloadFormat = this.supportedDownloadFormats[ 0 ];


		// multi format
		this.isMultiFormatDownloadButton = this.supportedDownloadFormats.length > 1;

		// icon
		if ( ObjectUtils.isNullish( this.showIcon ) ) { // after isMultiFormatDownloadButton is resolved
			this.showIcon = this.isMultiFormatDownloadButton;
		}
	}

	updateDownloadButtonCollapsedState( collapse?: boolean ): void {
		// skip if it's not multiFormatDownloadButton
		if ( !this.isMultiFormatDownloadButton ) {
			return;
		}

		// sets desired collapse state (mouseEvents, clickOutside )
		if ( [ false, true ].includes( collapse ) ) {
			this.multiFormatDownloadButtonIsCollapsed = collapse;
			return;
		}

		// toggles collapse state (primary button click ) and collapse on formatButton click (via event bubbling)
		this.multiFormatDownloadButtonIsCollapsed = !this.multiFormatDownloadButtonIsCollapsed;
	}

	emitButtonEvent() {
		// toggles collapsed state when format is not selected (primary button)
		// format is selected (by format button) and cleared after event emit, the collapse state is set to true via event bubbling call
		if ( this.isMultiFormatDownloadButton && !this.selectedDownloadFormat ) {
			this.updateDownloadButtonCollapsedState();
			return;
		}

		// emits null action, when no supported format exist
		if ( !this.isMultiFormatDownloadButton ) {
			this.selectedDownloadFormat = this._defaultDownloadFormat;
		}

		// either emits selected format on multi format mode or if exist the default format on single format mode
		if ( this.selectedDownloadFormat ) {
			this.buttonEvent.emit( this.selectedDownloadFormat );
			this.selectedDownloadFormat = null;
			return;
		}

		// emits null action, when no supported format exist
		this.buttonEvent.emit( null );
	}
}
